import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  kadryPlural,
  type KadryBotStatus,
  type KadryOverview,
  type KadryOverviewRequestRef,
  type KadrySourceAccount,
} from '@entities/kadry';
import { Panel, PanelHead, toneColor, type Tone } from './ui';

/* «Требует внимания»: тревоги и то, что мешает подбору, — в порядке
   срочности. Пусто — зелёная строка «всё под контролем». */

type ItemTone = Extract<Tone, 'danger' | 'warning' | 'info' | 'success'>;

interface Item {
  key: string;
  tone: ItemTone;
  title: string;
  sub: string;
  to: string;
  /** Заявки пункта: переход ведёт в первую, остальные — кнопками ниже. */
  refs?: KadryOverviewRequestRef[];
}

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled.button<{ $tone: ItemTone }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  border-left: 3px solid ${({ theme, $tone }) => toneColor(theme, $tone)};
  background: ${({ theme }) => theme.colors.surface};
  transition:
    border-color 120ms ease,
    transform 120ms ease;

  &:hover {
    border-color: ${({ theme, $tone }) => toneColor(theme, $tone)};
    transform: translateX(2px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const StaticRow = styled(Row)`
  cursor: default;

  &:hover {
    border-color: transparent;
    transform: none;
  }
`;

const Dot = styled.span<{ $tone: ItemTone }>`
  width: 8px;
  height: 8px;
  margin-top: 6px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ theme, $tone }) => toneColor(theme, $tone)};
`;

const Title = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Entry = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Refs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding-left: ${({ theme }) => theme.spacing(8)};
`;

const RefButton = styled.button`
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 2px ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const CANDIDATES = ['кандидат', 'кандидата', 'кандидатов'] as const;

/** Площадки, на которых агент ищет и приглашает. */
const PLATFORMS = [
  { id: 'hh', title: 'hh.ru', paid: 'приглашения из поиска' },
  { id: 'rabota', title: 'Работа.ру', paid: 'открытие контактов' },
] as const;
const REQUESTS = '/agents/kadry/requests';
const INTERESTED = '/agents/kadry/interested';
const CONNECTIONS = '/agents/kadry/settings/connections';
const requestPath = (id: string) => `${REQUESTS}/${id}`;

/** Переход в заявку пункта; заявок нет в ответе — список заявок. */
function toRequests(refs: KadryOverviewRequestRef[]): Pick<Item, 'to' | 'refs'> {
  return refs.length > 0 ? { to: requestPath(refs[0].id), refs } : { to: REQUESTS };
}

/** «Заявка «…»» или «в N заявках» — перед пояснением пункта. */
function where(refs: KadryOverviewRequestRef[]): string {
  if (refs.length === 1) return `Заявка «${refs[0].title}». `;
  if (refs.length > 1) return `${refs.length} ${kadryPlural(refs.length, ['заявка', 'заявки', 'заявок'])} — ниже. `;
  return '';
}

function botItem(b: KadryBotStatus): Item | null {
  const name = b.channel === 'telegram' ? 'Telegram' : 'MAX';
  if (b.bot && b.enabled) return null;
  if (b.bot && !b.enabled) {
    return {
      key: `bot-off-${b.channel}`,
      tone: 'info',
      title: `Бот ${name} выключен`,
      sub: 'Предложения в мессенджер не уходят — включите канал в «Функциях»',
      to: '/agents/kadry/settings/gates',
    };
  }
  if (b.problem === 'unreachable') {
    return {
      key: `bot-down-${b.channel}`,
      tone: 'warning',
      title: `Бот ${name}: нет связи`,
      sub: 'Токен сохранён, но сервер не достучался до мессенджера',
      to: CONNECTIONS,
    };
  }
  return {
    key: `bot-none-${b.channel}`,
    tone: 'info',
    title: b.token_set ? `Бот ${name}: токен не принят` : `Бот ${name} не подключён`,
    sub: 'Кандидаты смогут ответить только по ссылке из приглашения hh.ru',
    to: CONNECTIONS,
  };
}

export function attentionItems(
  o: KadryOverview,
  setup: { sources?: KadrySourceAccount[]; bots?: KadryBotStatus[] } | null,
): Item[] {
  const out: Item[] = [];
  const r = o.responses;
  if (r.not_contacted > 0) {
    out.push({
      key: 'not-contacted',
      tone: 'danger',
      title: `${r.not_contacted} ${kadryPlural(r.not_contacted, CANDIDATES)}: «со мной не связались»`,
      sub: 'Ответили «Подходит», через 5 дней сказали боту, что звонка не было — позвоните',
      to: `${INTERESTED}?show=not_contacted`,
    });
  }
  if (r.waiting_call > 0) {
    out.push({
      key: 'waiting-call',
      tone: 'warning',
      title: `${r.waiting_call} ${kadryPlural(r.waiting_call, CANDIDATES)} ждут звонка`,
      sub: 'Ответили «Подходит» — после разговора отметьте «Связались»',
      to: `${INTERESTED}?show=waiting`,
    });
  }
  const failed = o.deliveries.reduce((s, d) => s + d.failed, 0);
  if (failed > 0) {
    out.push({
      key: 'failed',
      tone: 'warning',
      title: `Не доставлено предложений: ${failed}`,
      sub: `${where(o.attention.failed_deliveries)}Причина — в реестре заявки у кандидата`,
      ...toRequests(o.attention.failed_deliveries),
    });
  }
  const unknown = o.deliveries.reduce((s, d) => s + d.unknown, 0);
  if (unknown > 0) {
    out.push({
      key: 'unknown',
      tone: 'warning',
      title: `Исход отправки неизвестен: ${unknown}`,
      sub: `${where(o.attention.unknown_deliveries)}Площадка не ответила вовремя — проверьте, дошло ли сообщение`,
      ...toRequests(o.attention.unknown_deliveries),
    });
  }
  if (o.requests.active_without_profile > 0) {
    const n = o.requests.active_without_profile;
    out.push({
      key: 'no-profile',
      tone: 'warning',
      title: `${n} ${kadryPlural(n, ['активная заявка', 'активные заявки', 'активных заявок'])} без профиля поиска`,
      sub: `${where(o.attention.no_profile)}Поиск по ним не идёт — утвердите профиль в карточке заявки`,
      ...toRequests(o.attention.no_profile),
    });
  }
  if (o.search.failed_24h > 0) {
    out.push({
      key: 'search-failed',
      tone: 'warning',
      title: `Поиск завершился ошибкой: ${o.search.failed_24h} за сутки`,
      sub: `${where(o.attention.search_failed)}Причина — в истории поиска в карточке заявки`,
      ...toRequests(o.attention.search_failed),
    });
  }
  if (setup) {
    for (const src of PLATFORMS) {
      const acc = setup.sources?.find((s) => s.source === src.id);
      if (acc && acc.status !== 'connected') {
        out.push({
          key: `source-${src.id}`,
          tone: 'info',
          title: `${src.title} не подключён`,
          sub: `Без подключения агент не ищет кандидатов на ${src.title} и не отправляет там приглашения`,
          to: CONNECTIONS,
        });
      }
    }
    for (const b of setup.bots ?? []) {
      const item = botItem(b);
      if (item) out.push(item);
    }
    for (const b of o.budgets.filter((x) => !x.set)) {
      const src = PLATFORMS.find((p) => p.id === b.source);
      out.push({
        key: `budget-${b.source}`,
        tone: 'info',
        title: `Бюджет ${src?.title ?? b.source} на месяц не задан`,
        sub: `Платные действия (${src?.paid ?? 'платные операции'}) будут отклоняться, пока бюджет не задан`,
        to: CONNECTIONS,
      });
    }
  }
  return out;
}

export function AttentionPanel({ items }: { items: Item[] }) {
  const navigate = useNavigate();
  const urgent = items.filter((i) => i.tone === 'danger' || i.tone === 'warning').length;
  return (
    <Panel>
      <PanelHead>
        <h3>Требует внимания</h3>
        <span>{urgent > 0 ? `срочных: ${urgent}` : 'срочного нет'}</span>
      </PanelHead>
      <List>
        {items.length === 0 ? (
          <StaticRow as="div" $tone="success">
            <Dot $tone="success" />
            <div>
              <Title>Всё под контролем</Title>
              <Sub>Тревог нет, все кандидаты с ответом «Подходит» получили звонок.</Sub>
            </div>
          </StaticRow>
        ) : (
          items.map((i) => (
            <Entry key={i.key}>
              <Row type="button" $tone={i.tone} onClick={() => navigate(i.to)}>
                <Dot $tone={i.tone} />
                <div>
                  <Title>{i.title}</Title>
                  <Sub>{i.sub}</Sub>
                </div>
              </Row>
              {i.refs && i.refs.length > 1 && (
                <Refs>
                  {i.refs.map((r) => (
                    <RefButton
                      key={r.id}
                      type="button"
                      title={r.title}
                      onClick={() => navigate(requestPath(r.id))}
                    >
                      {r.title}
                    </RefButton>
                  ))}
                </Refs>
              )}
            </Entry>
          ))
        )}
      </List>
    </Panel>
  );
}
